import { DateRange } from "react-day-picker";

import { Search, X } from "lucide-react";

import { CalendarDateRangePicker } from "~/components/date-range-picker";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { useTransportistas } from "~/hooks/useTransportistas";

interface SellerOption {
  id: string;
  name: string;
  lastname: string;
}

interface Props {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  selectedSeller: string;
  setSelectedSeller: (sellerId: string) => void;
  sellers: SellerOption[];
  selectedGroup?: string;
  setSelectedGroup?: (group: string) => void;
  groups?: string[];
  currentTab?: string;
  dateRange?: DateRange;
  setDateRange?: (range: DateRange | undefined) => void;
  selectedTransportista?: string;
  setSelectedTransportista?: (transportistaId: string) => void;
  onClearFilters?: () => void;
}

const TABS_WITH_DATE_FILTER = ["facturado", "in_transit", "delivered"];
const TABS_WITH_TRANSPORTISTA_FILTER = ["in_transit", "delivered"];

export function OperationsOrdersFilterBar({
  searchQuery,
  setSearchQuery,
  selectedSeller,
  setSelectedSeller,
  sellers,
  selectedGroup,
  setSelectedGroup,
  groups,
  currentTab,
  dateRange,
  setDateRange,
  selectedTransportista,
  setSelectedTransportista,
  onClearFilters,
}: Props) {
  const { transportistas, isLoading: isLoadingTransportistas } =
    useTransportistas();

  const showDateFilter =
    currentTab && TABS_WITH_DATE_FILTER.includes(currentTab);
  const showTransportistaFilter =
    currentTab && TABS_WITH_TRANSPORTISTA_FILTER.includes(currentTab);

  const hasActiveFilters =
    dateRange?.from ||
    (selectedTransportista && selectedTransportista !== "all") ||
    (selectedGroup && selectedGroup !== "all");

  return (
    <div className="flex flex-wrap items-end gap-4">
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-muted-foreground">
          Buscar
        </label>
        <div className="relative w-64">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Cliente, RUT o Nº factura"
            className="h-9 pl-8"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-muted-foreground">
          Vendedor
        </label>
        <div className="w-52">
          <Select value={selectedSeller} onValueChange={setSelectedSeller}>
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Todos los vendedores" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los vendedores</SelectItem>
              {sellers.map((seller) => (
                <SelectItem key={seller.id} value={seller.id}>
                  {seller.name} {seller.lastname}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {setSelectedGroup && groups && groups.length > 0 && (
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">
            Grupo
          </label>
          <div className="w-52">
            <Select
              value={selectedGroup ?? "all"}
              onValueChange={setSelectedGroup}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Todos los grupos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los grupos</SelectItem>
                {groups.map((group) => (
                  <SelectItem key={group} value={group}>
                    {group}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {showDateFilter && setDateRange && (
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">
            Fecha de factura
          </label>
          <CalendarDateRangePicker
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
          />
        </div>
      )}

      {showTransportistaFilter && setSelectedTransportista && (
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">
            Transportista
          </label>
          <div className="w-52">
            <Select
              value={selectedTransportista}
              onValueChange={setSelectedTransportista}
              disabled={isLoadingTransportistas}
            >
              <SelectTrigger className="h-9">
                <SelectValue
                  placeholder={
                    isLoadingTransportistas
                      ? "Cargando..."
                      : "Todos los transportistas"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los transportistas</SelectItem>
                {transportistas.map((transportista) => (
                  <SelectItem key={transportista.id} value={transportista.id}>
                    {transportista.name} {transportista.lastname}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {hasActiveFilters && onClearFilters && (
        <Button
          variant="ghost"
          size="sm"
          className="h-9"
          onClick={onClearFilters}
        >
          <X className="mr-1 h-4 w-4" />
          Limpiar filtros
        </Button>
      )}
    </div>
  );
}
